import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import {
    Button,
    Container,
    Typography,
    Box,
    Alert,
    LinearProgress,
    IconButton,
    Snackbar,
    Grid,
    Card,
    CardContent,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Fade,
    useTheme,
    alpha,
    FormControl,
    Select,
    MenuItem,
    Paper,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import {
    ArrowBack,
    ArrowForward,
    Info as InfoIcon,
    CheckCircle as CheckCircleIcon,
    NavigateNext as NavigateNextIcon,
    NavigateBefore as NavigateBeforeIcon,
    Language as LanguageIcon,
    ArrowForwardIos as ArrowForwardIosIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import BackButton from "./BackButton";
import { useTranslation } from 'react-i18next';
import OTReassessmentForm from './OTReassessmentForm';
import SpeechReassessmentForm from './SpeechReassessmentForm';
import BehavioralReassessmentForm from './BehavioralReassessmentForm';

// --- INITIAL ASSESSMENT DATA (M-CHAT/SRS based) ---
const getInitialSections = (t) => [
    { 
        key: 'a', 
        title: t('section_a_social_communication'), 
        icon: '👥', 
        questions: [
            t('question_ eye_contact'),
            t('question_smile_response'),
            t('question_point_interest'),
            t('question_conversation'),
            t('question_name_response'),
            t('question_pretend_play'),
            t('question_share_objects'),
            t('question_personal_space'),
            t('question_adapt_behavior'),
            t('question_gestures')
        ], 
        maxScore: 40 
    },
    { 
        key: 'b', 
        title: t('section_b_repetitive_behavior'), 
        icon: '🔄', 
        questions: [
            t('question_repetitive_movements'),
            t('question_sensory_sensitivity'),
            t('question_insist_routines'),
            t('question_intense_interest'),
            t('question_line_up_objects'),
            t('question_echolalia'),
            t('question_unusual_attachment'),
            t('question_fixated_parts')
        ], 
        maxScore: 32 
    },
    { 
        key: 'c', 
        title: t('section_c_inattention_executive_function'), 
        icon: '🧠', 
        questions: [
            t('question_lose_focus'),
            t('question_following_instructions'),
            t('question_forget_tasks'),
            t('question_avoid_effort'),
            t('question_jump_activities'),
            t('question_misplace_items'),
            t('question_careless_mistakes')
        ], 
        maxScore: 28 
    },
    { 
        key: 'd', 
        title: t('section_d_hyperactivity_impulsivity'), 
        icon: '⚡', 
        questions: [
            t('question_fidget_squirm'),
            t('question_run_climb'),
            t('question_talk_excessive'),
            t('question_interrupt_intrude'),
            t('question_blurt_out'),
            t('question_waiting_turn')
        ], 
        maxScore: 24 
    },
    {
        key: 'e',
        title: t('section_e_speech_communication'),
        icon: '🗣️',
        questions: [
            t('question_speech_name_response'),
            t('question_speech_point_objects'),
            t('question_speech_follow_directions'),
            t('question_speech_get_objects_room'),
            t('question_speech_point_body_parts'),
            t('question_speech_point_pictures'),
            t('question_speech_answer_questions'),
            t('question_speech_point_family'),
            t('question_speech_understand_prepositions'),
            t('question_speech_understand_color_size'),
            t('question_speech_pretend_play'),
            t('question_speech_ignore_speaking'),
            t('question_speech_socialize_play'),
            t('question_speech_difficulty_understanding'),
            t('question_speech_frustrated_not_understood'),
            t('question_speech_repeat_sounds')
        ],
        maxScore: 64
    }
];

const getInitialResponseOptions = (t, sectionKey = 'other') => {
    if (sectionKey === 'a') {
        return [
            { value: 4, label: t('score_never'), emoji: '😢', color: '#ef4444', subtitle: 'Highly concerning' },
            { value: 3, label: t('score_rarely'), emoji: '😟', color: '#f97316', subtitle: 'Very concerning' },
            { value: 2, label: t('score_sometimes'), emoji: '😐', color: '#f59e0b', subtitle: 'Moderately concerning' },
            { value: 1, label: t('score_often'), emoji: '🙂', color: '#84cc16', subtitle: 'Slightly concerning' },
            { value: 0, label: t('score_always'), emoji: '😊', color: '#10b981', subtitle: 'Less concerning' }
        ];
    }
    return [
        { value: 0, label: t('score_never'), emoji: '😊', color: '#10b981', subtitle: 'Less concerning' },
        { value: 1, label: t('score_rarely'), emoji: '🙂', color: '#84cc16', subtitle: 'Slightly concerning' },
        { value: 2, label: t('score_sometimes'), emoji: '😐', color: '#f59e0b', subtitle: 'Moderately concerning' },
        { value: 3, label: t('score_often'), emoji: '😟', color: '#f97316', subtitle: 'Very concerning' },
        { value: 4, label: t('score_always'), emoji: '😢', color: '#ef4444', subtitle: 'Highly concerning' }
    ];
};

// --- Custom Components for Redesign ---
const FlashcardOption = ({ option, isSelected, onClick }) => {
    return (
        <Card 
            onClick={onClick}
            elevation={isSelected ? 4 : 0}
            sx={{ 
                cursor: 'pointer',
                borderRadius: '16px',
                minWidth: { xs: '80px', sm: '120px' },
                transition: 'all 0.2s ease-in-out',
                border: isSelected ? `2px solid ${option.color}` : '2px solid transparent',
                bgcolor: isSelected ? alpha(option.color, 0.05) : 'background.paper',
                transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    borderColor: alpha(option.color, 0.5)
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                p: { xs: 1.5, sm: 2 }
            }}
        >
            <Typography variant="h3" sx={{ mb: 1, filter: isSelected ? 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' : 'none' }}>
                {option.emoji}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: isSelected ? option.color : 'text.primary', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                {option.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' }, mt: 0.5, textAlign: 'center' }}>
                {option.subtitle}
            </Typography>
        </Card>
    );
};

const AutismADHDAssessment = () => {
    const { t, i18n } = useTranslation();
    const { childId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    
    const [loading, setLoading] = useState(true);
    const [assessmentType, setAssessmentType] = useState('initial'); // 'initial', 'ot_reassessment', or 'speech_reassessment'
    const [answers, setAnswers] = useState([]);
    const [currentSection, setCurrentSection] = useState(0);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [step, setStep] = useState(1);
    const [scores, setScores] = useState({ s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, srs_score: 0, mchat_score: 0 });
    const [result, setResult] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [animationKey, setAnimationKey] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    const initialSections = getInitialSections(t);
    const initialResponseOptions = getInitialResponseOptions(t, initialSections[currentSection]?.key);

    const changeLanguage = (event) => {
        i18n.changeLanguage(event.target.value);
    };

    useEffect(() => {
        const fetchPastAssessments = async () => {
            const userRole = localStorage.getItem('userRole');
            
            // Set default based on role
            if (userRole === 'occupational') {
                setAssessmentType('ot_reassessment');
                setLoading(false);
                return;
            } else if (userRole === 'speech') {
                setAssessmentType('speech_reassessment');
                setLoading(false);
                return;
            } else if (userRole === 'behavioral') {
                setAssessmentType('behavioral_reassessment');
                setLoading(false);
                return;
            }

            try {
                const res = await axios.get(`${API_BASE_URL}/get_assessments.php?child_id=${childId}`);
                if (res.data && res.data.length > 0) {
                    setAssessmentType('ot_reassessment'); // Default fallback for other roles
                } else {
                    setAssessmentType('initial');
                    setAnswers(Array(47).fill(null));
                }
            } catch (error) {
                console.error("Error fetching assessments:", error);
                setAssessmentType('initial');
                setAnswers(Array(47).fill(null));
            } finally {
                setLoading(false);
            }
        };
        fetchPastAssessments();
    }, [childId]);

    const triggerAnimation = () => setAnimationKey(prev => prev + 1);

    const handleAnswerChange = (value) => {
        const questionIndex = initialSections[currentSection].questions.indexOf(initialSections[currentSection].questions[currentQuestion]) + initialSections.slice(0, currentSection).reduce((sum, s) => sum + s.questions.length, 0);
        const newAnswers = [...answers];
        newAnswers[questionIndex] = parseInt(value);
        setAnswers(newAnswers);

        const sectionQuestions = initialSections[currentSection].questions.length;
        if (currentQuestion < sectionQuestions - 1) {
            setTimeout(() => {
                triggerAnimation();
                setCurrentQuestion(currentQuestion + 1);
            }, 300); // Small delay to let user see selection
        }
    };

    const getAnsweredCount = (sectionIndex) => {
        const start = initialSections.slice(0, sectionIndex).reduce((sum, s) => sum + s.questions.length, 0);
        const end = start + initialSections[sectionIndex].questions.length;
        return answers.slice(start, end).filter(a => a !== null).length;
    };

    const isSectionComplete = (sectionIndex) => getAnsweredCount(sectionIndex) === initialSections[sectionIndex].questions.length;

    const handleNextSection = async () => {
        if (currentSection < initialSections.length - 1) {
            triggerAnimation();
            setCurrentSection(currentSection + 1);
            setCurrentQuestion(0);
        } else {
            await handleSubmitInitial();
            setStep(2);
        }
    };

    const handlePrevSection = () => {
        if (currentSection > 0) {
            triggerAnimation();
            setCurrentSection(currentSection - 1);
            setCurrentQuestion(0);
        }
    };

    const handleNextQuestion = () => {
        if (currentQuestion < initialSections[currentSection].questions.length - 1) {
            triggerAnimation();
            setCurrentQuestion(currentQuestion + 1);
        }
    };

    const handlePrevQuestion = () => {
        if (currentQuestion > 0) {
            triggerAnimation();
            setCurrentQuestion(currentQuestion - 1);
        }
    };


    const calculateScoresInitial = () => {
        // Now Section A (social) is also inverted in UI, so higher value = more concern
        const social = answers.slice(0, 10).reduce((sum, val) => sum + (val || 0), 0);
        const repetitive = answers.slice(10, 18).reduce((sum, val) => sum + (val || 0), 0);
        const inattention = answers.slice(18, 25).reduce((sum, val) => sum + (val || 0), 0);
        const hyperactivity = answers.slice(25, 31).reduce((sum, val) => sum + (val || 0), 0);
        const speech = answers.slice(31, 47).reduce((sum, val) => sum + (val || 0), 0);
        
        const srs_score = social + repetitive;
        const mchat_score = social;

        return { s1: social, s2: repetitive, s3: inattention, s4: hyperactivity, s5: speech, srs_score, mchat_score };
    };
    const interpretResultsInitial = (scores) => {
        const { s1: social, s2: repetitive, s3: inattention, s4: hyperactivity, s5: speech } = scores;
        
        // Revised thresholds: social (max 40), repetitive (max 32), inattention (max 28), hyperactivity (max 24)
        // High = More concerning traits
        const hasAutismTraits = social > 12 || repetitive > 10;
        const hasADHDTraits = inattention > 14 || hyperactivity > 12;
        
        const isAutism = social > 18 && repetitive > 14; // Strict Autism markers
        const isADHD = inattention > 18 && hyperactivity > 16; // Strict ADHD markers
        const needsSpeech = speech > 20;

        if (isAutism && isADHD) return { interpretation: t('co_occurrence_autism_adhd'), risk: 'High', insights: ['Behavioral Support', 'ADHD Evaluation', 'Specialized Therapy', ...(needsSpeech ? ['Speech Therapy'] : [])] };
        if (isAutism) return { interpretation: t('likely_autism'), risk: 'High', insights: ['Behavioral Support', 'Speech Therapy', 'Social Skills Training'] };
        if (isADHD) return { interpretation: t('likely_adhd'), risk: 'High', insights: ['ADHD Evaluation', 'Behavioral Therapy', 'Educational Support', ...(needsSpeech ? ['Speech Therapy Consultation'] : [])] };
        
        if (hasAutismTraits || hasADHDTraits) {
            let desc = "Emerging Traits Noted";
            if (hasAutismTraits && hasADHDTraits) desc = "Emerging Autism & ADHD Traits";
            else if (hasAutismTraits) desc = "Emerging Autism Traits";
            else desc = "Emerging ADHD Traits";
            
            return { interpretation: desc, risk: 'Moderate', insights: ['Focus on Social Engagement', 'Monitor Behavioral Patterns', 'Follow-up Assessment in 3 months', ...(needsSpeech ? ['Speech Therapy Consultation'] : [])] };
        }

        return { interpretation: t('no_concern'), risk: 'Low', insights: ['Continue Supportive Environment', 'Regular Check-ups'] };
    };

    const [recommendedTherapies, setRecommendedTherapies] = useState([]);

    const handleSubmitInitial = async () => {
        if (submitting) return;
        setSubmitting(true);

        const scores = calculateScoresInitial();
        const result = interpretResultsInitial(scores);
        setScores(scores);
        setResult(result);

        const data = {
            child_id: childId,
            assessment_type: assessmentType,
            answers,
            srs_score: scores.srs_score,
            mchat_score: scores.mchat_score,
            social_communication_score: scores.s1,
            repetitive_behavior_score: scores.s2,
            inattention_score: scores.s3,
            hyperactivity_score: scores.s4,
            speech_score: scores.s5,
            interpretation: result.interpretation,
            recommended_therapies: recommendedTherapies
        };

        try {
            await axios.post(`${API_BASE_URL}/submit_assessment.php`, data);
            setSnackbar({ open: true, message: 'Assessment completed successfully!', severity: 'success' });
            setTimeout(() => navigate(`/child-profile/${childId}`), 2000);
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to complete assessment. Please try again.', severity: 'error' });
            setSubmitting(false);
        }
    };

    const handleSubmitSpeech = async (speechData) => {
        if (submitting) return;
        setSubmitting(true);

        const s1 = (speechData.receptiveLanguage.respondsToName + speechData.receptiveLanguage.followsOneStep + speechData.receptiveLanguage.followsTwoStep + speechData.receptiveLanguage.identifiesObjects + speechData.receptiveLanguage.understandsBodyParts + speechData.receptiveLanguage.understandsWhQuestions +
                   speechData.expressiveLanguage.usesSoundsWords + speechData.expressiveLanguage.usesGestures + speechData.expressiveLanguage.vocabularySize + speechData.expressiveLanguage.formsPhrases + speechData.expressiveLanguage.formsSentences + speechData.expressiveLanguage.namingObjects);
        
        const s2 = (speechData.speechSkills.soundProduction + speechData.speechSkills.pronunciationClarity + speechData.speechSkills.fluency + speechData.speechSkills.voiceQuality +
                   speechData.socialCommunication.initiatesCommunication + speechData.socialCommunication.maintainsConversation + speechData.socialCommunication.turnTaking + speechData.socialCommunication.appropriateExpressions);
        
        const s3 = (speechData.behavioralAttention.attentionSpan + speechData.behavioralAttention.sittingTolerance + speechData.behavioralAttention.compliance + speechData.behavioralAttention.responseToTherapyCues +
                   speechData.playInteraction.functionalPlay + speechData.playInteraction.imitationSkills + speechData.playInteraction.jointAttention);
        
        const s4 = (speechData.oralMotor.lipMovement + speechData.oralMotor.tongueMovement + speechData.oralMotor.jawControl + speechData.oralMotor.blowingSucking +
                   speechData.parentFeedback.responseToName + speechData.parentFeedback.eyeContact + speechData.parentFeedback.expressNeeds + speechData.parentFeedback.understandInstructions + speechData.parentFeedback.socialInteraction + speechData.parentFeedback.speechClarity + speechData.parentFeedback.vocabularyImprovement);

        const data = {
            child_id: childId,
            assessment_type: 'speech_reassessment',
            answers: speechData,
            social_communication_score: Math.min(s1, 40), // Capped to match chart UI
            repetitive_behavior_score: Math.min(s2, 32),
            inattention_score: Math.min(s3, 28),
            hyperactivity_score: Math.min(s4, 24),
            speech_score: s2, // Using speech & social as speech score
            interpretation: speechData.summary.interpretation
        };

        try {
            await axios.post(`${API_BASE_URL}/submit_assessment.php`, data);
            setSnackbar({ open: true, message: 'Speech Reassessment completed successfully!', severity: 'success' });
            setTimeout(() => navigate(`/child-profile/${childId}`), 2000);
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to complete Speech assessment.', severity: 'error' });
            setSubmitting(false);
        }
    };

    const handleSubmitOT = async (otData) => {
        if (submitting) return;
        setSubmitting(true);

        let physical = 0; let motor = 0; let behavior = 0; let adl = 0;
        
        const pA = otData.physicalAssessment || {};
        ["muscleToneUpper", "muscleToneLower", "equilibrium", "posturalTone", "atnr", "stnr", "tlr", "voluntaryUpper", "voluntaryLower"].forEach(k => { 
            if(pA[k] === "Normal") physical += 1.5; 
        });

        const fM = otData.fineMotor || {};
        ["coordination", "unilateral", "bilateral", "eyeHand"].forEach(k => { 
            if(fM[k]?.toString().toLowerCase().includes("good")) motor += 2; 
        });
        
        const pR = otData.prehensile || {};
        ["cylindricalRt", "cylindricalLf", "sphericalRt", "sphericalLf", "pincerRt", "pincerLf", "beading"].forEach(k => { 
            if(pR[k]?.toString().toLowerCase().includes("good")) motor += 1; 
        });
        
        const bA = otData.behavioralAttention || {};
        if(bA.commandFollowing?.toString().toLowerCase().includes("good")) behavior += 4;
        if(bA.attention?.toString().toLowerCase().includes("good")) behavior += 4;
        if(bA.hyperactivity?.toString().toLowerCase().includes("reduced")) behavior += 4;
        
        const sC = otData.selfCare || {};
        if(sC.eating?.toString().toLowerCase().includes("independent")) adl += 3;
        if(sC.bathing?.toString().toLowerCase().includes("well")) adl += 3;
        if(sC.toileting?.toString().toLowerCase().includes("well")) adl += 3;
        if(sC.dressingLower?.toString().toLowerCase().includes("independent")) adl += 3;

        const data = {
            child_id: childId,
            assessment_type: 'ot_reassessment',
            answers: otData,
            social_communication_score: Math.min(15, Math.round(physical)),
            repetitive_behavior_score: Math.min(15, motor),
            inattention_score: Math.min(15, behavior),
            hyperactivity_score: Math.min(15, adl),
            interpretation: 'Occupational Therapy clinical reassessment completed.'
        };

        try {
            await axios.post(`${API_BASE_URL}/submit_assessment.php`, data);
            setSnackbar({ open: true, message: 'OT Reassessment completed successfully!', severity: 'success' });
            setTimeout(() => navigate(`/child-profile/${childId}`), 2000);
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to complete OT assessment.', severity: 'error' });
            setSubmitting(false);
        }
    };

    const handleSubmitBehavioral = async (behavioralData) => {
        if (submitting) return;
        setSubmitting(true);

        // Heuristic scoring for behavioral data
        let social = 0; let repetitive = 0; let attention = 0; let control = 0;

        const aS = behavioralData.attentionSitting || {};
        if (aS.sustainsAttentionAfter === "Always" || aS.sustainsAttentionAfter === "Sometimes") attention += 5;
        if (aS.easilyDistractedAfter === "Rarely" || aS.easilyDistractedAfter === "Never") attention += 5;

        const cL = behavioralData.compliance || {};
        if (cL.simpleInstructionsAfter === "Always" || cL.simpleInstructionsAfter === "Sometimes") control += 5;

        const sB = behavioralData.socialBehavior || {};
        if (sB.eyeContactAfter === "Always" || sB.eyeContactAfter === "Sometimes") social += 5;
        if (sB.respondsToNameAfter === "Always" || sB.respondsToNameAfter === "Sometimes") social += 5;

        const rB = behavioralData.repetitiveBehavior || {};
        if (rB.handFlappingAfter === "Never" || rB.handFlappingAfter === "Rare") repetitive += 5;
        if (rB.verbalStemmingAfter === "Never" || rB.verbalStemmingAfter === "Rare") repetitive += 5;

        const data = {
            child_id: childId,
            assessment_type: 'behavioral_reassessment',
            answers: behavioralData,
            social_communication_score: Math.min(40, social * 4),
            repetitive_behavior_score: Math.min(32, repetitive * 3),
            inattention_score: Math.min(28, attention * 2),
            hyperactivity_score: Math.min(24, control * 2),
            interpretation: behavioralData.summaries.overallSummary || 'Behavioral clinical reassessment completed.'
        };

        try {
            await axios.post(`${API_BASE_URL}/submit_assessment.php`, data);
            setSnackbar({ open: true, message: 'Behavioral Reassessment completed successfully!', severity: 'success' });
            setTimeout(() => navigate(`/child-profile/${childId}`), 2000);
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to complete Behavioral assessment.', severity: 'error' });
            setSubmitting(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress size={60} thickness={4} /></Box>;
    }

    if (assessmentType === 'ot_reassessment') {
        return (
            <Container maxWidth="xl" sx={{ py: 6 }}>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 1, color: 'primary.main' }}>
                    Occupational Therapy Reassessment
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ mb: 6 }}>
                    Comprehensive clinical documentation for continuing care.
                </Typography>
                <OTReassessmentForm onSubmit={handleSubmitOT} submitting={submitting} />
            </Container>
        );
    }

    if (assessmentType === 'speech_reassessment') {
        return (
            <Container maxWidth="xl" sx={{ py: 6 }}>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 1, color: '#0097a7' }}>
                    Speech Therapy Reassessment
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ mb: 6 }}>
                    Clinical monitoring of language, articulation, and oral motor skills.
                </Typography>
                <SpeechReassessmentForm onSubmit={handleSubmitSpeech} submitting={submitting} />
            </Container>
        );
    }

    if (assessmentType === 'behavioral_reassessment') {
        return (
            <Container maxWidth="xl" sx={{ py: 6 }}>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 1, color: '#673ab7' }}>
                    Behavioral Therapy Reassessment
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ mb: 6 }}>
                    Clinical assessment of behavior, attention, regulation, and functional skills.
                </Typography>
                <BehavioralReassessmentForm onSubmit={handleSubmitBehavioral} submitting={submitting} />
            </Container>
        );
    }

    // --- RENDER INITIAL ASSESSMENT ---
    const currentSectionData = initialSections[currentSection];
    const answeredCount = getAnsweredCount(currentSection);
    const progressPercent = (answeredCount / currentSectionData.questions.length) * 100;

    if (step === 1) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <BackButton />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LanguageIcon sx={{ color: 'text.secondary' }} />
                        <FormControl variant="standard" sx={{ minWidth: 60 }}>
                            <Select
                                value={i18n.language}
                                onChange={changeLanguage}
                                disableUnderline
                                sx={{ '.MuiSelect-select': { p: 0, pr: '24px !important' } }}
                            >
                                <MenuItem value="en">EN</MenuItem>
                                <MenuItem value="te">TE</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Header & Progress Bar */}
                <Box sx={{ mb: 6, textAlign: 'center' }}>
                    <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
                        {t('autism_adhd_assessment_title')}
                    </Typography>
                    
                    <Card elevation={0} sx={{ p: 2, borderRadius: 4, display: 'inline-block', width: { xs: '100%', sm: 'auto' }, minWidth: { sm: '50%' }, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{currentSectionData.icon}</span> {currentSectionData.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                {answeredCount} / {currentSectionData.questions.length} {t('complete_text')}
                            </Typography>
                        </Box>
                        <LinearProgress 
                            variant="determinate" 
                            value={progressPercent} 
                            sx={{ height: 10, borderRadius: 5, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                        />
                    </Card>
                </Box>

                {/* Main Flashcard Interface */}
                <Fade in={true} key={animationKey} timeout={600}>
                    <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                        
                        <Card elevation={4} sx={{ borderRadius: '24px', overflow: 'visible', mb: 4, bgcolor: '#ffffff' }}>
                            {/* Question Header */}
                            <Box sx={{ p: { xs: 2, md: 4 }, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="overline" sx={{ letterSpacing: 1.5, color: 'primary.main', fontWeight: 'bold' }}>
                                    {t('question_text', { number: currentQuestion + 1 })} / {currentSectionData.questions.length}
                                </Typography>
                                <Chip label={`${t('step_indicator', { current: currentSection + 1, total: initialSections.length })}`} size="small" color="secondary" variant="outlined" />
                            </Box>
                            
                            {/* Question Body */}
                            <Box sx={{ p: { xs: 3, md: 5 }, minHeight: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="h4" sx={{ textAlign: 'center', lineHeight: 1.4, fontWeight: 500, color: 'text.primary', fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                                    "{currentSectionData.questions[currentQuestion]}"
                                </Typography>
                            </Box>

                            {/* Response Options Horizontal Scroll */}
                            <Box sx={{ p: { xs: 2, md: 4 }, pt: 0, pb: { xs: 3, md: 4 } }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'row', 
                                    gap: 2, 
                                    overflowX: 'auto', 
                                    pb: 1,
                                    justifyContent: { xs: 'flex-start', md: 'center' },
                                    scrollSnapType: 'x mandatory',
                                    '&::-webkit-scrollbar': { height: '6px' },
                                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' }
                                }}>
                                    {initialResponseOptions.map((option) => (
                                        <Box key={option.value} sx={{ scrollSnapAlign: 'start', flexShrink: 0 }}>
                                            <FlashcardOption 
                                                option={option} 
                                                isSelected={answers[initialSections.slice(0, currentSection).reduce((sum, s) => sum + s.questions.length, 0) + currentQuestion] === option.value}
                                                onClick={() => handleAnswerChange(option.value)}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Card>

                        {/* Navigation Controls */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Button
                                    variant="outlined"
                                    onClick={handlePrevSection}
                                    disabled={currentSection === 0}
                                    sx={{ mr: 1, borderRadius: '12px' }}
                                >
                                    {t('previous_button')}
                                </Button>
                                <IconButton 
                                    onClick={handlePrevQuestion} 
                                    disabled={currentQuestion === 0}
                                    sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
                                >
                                    <NavigateBeforeIcon />
                                </IconButton>
                            </Box>

                            <Box>
                                <IconButton 
                                    onClick={handleNextQuestion} 
                                    disabled={currentQuestion === currentSectionData.questions.length - 1}
                                    sx={{ bgcolor: 'background.paper', boxShadow: 1, mr: 1 }}
                                >
                                    <NavigateNextIcon />
                                </IconButton>
                                <Button
                                    variant="contained"
                                    onClick={handleNextSection}
                                    disabled={!isSectionComplete(currentSection)}
                                    sx={{ borderRadius: '12px', minWidth: '150px' }}
                                >
                                    {currentSection === initialSections.length - 1 ? t('finish_assessment_button') : t('next_section_button')}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Fade>

                <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    <Alert severity={snackbar.severity} sx={{ width: '100%', borderRadius: 3 }}>{snackbar.message}</Alert>
                </Snackbar>
            </Container>
        );
    }

    // Calculate Autism Percentage for Chart
    const totalAutismPotential = (initialSections[0]?.maxScore || 40) + (initialSections[1]?.maxScore || 32);
    const autismScore = (scores.s1 || 0) + (scores.s2 || 0);
    const autismPercentage = totalAutismPotential > 0 ? Math.round((autismScore / totalAutismPotential) * 100) : 0;

    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {t('behavioral_insights_results_title')}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                    {t('behavioral_insights_results_subtitle')}
                </Typography>
            </Box>

            <Grid container spacing={4}>
                {/* Autism Percentage Chart Card */}
                <Grid item xs={12} md={6}>
                    <Card elevation={0} sx={{ p: 4, borderRadius: '24px', height: '100%', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
                            {t('autism_probability_index')}
                        </Typography>
                        
                        <Box sx={{ width: '100%', height: 300 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={[
                                            { name: t('autism_traits'), value: autismPercentage, color: '#ef4444' },
                                            { name: t('neurotypical_range'), value: 100 - autismPercentage, color: '#10b981' }
                                        ]}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={100}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        <Cell fill="#ef4444" />
                                        <Cell fill="#10b981" />
                                    </Pie>
                                    <RechartsTooltip />
                                    <Legend verticalAlign="bottom" height={36}/>
                                </PieChart>
                            </ResponsiveContainer>
                        </Box>

                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', color: autismPercentage > 50 ? 'error.main' : 'success.main' }}>
                                {autismPercentage}%
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {t('autism_traits')}
                            </Typography>
                        </Box>
                    </Card>
                </Grid>

                {/* Interpretation Card */}
                <Grid item xs={12} md={6}>
                    <Card elevation={0} sx={{ p: 4, borderRadius: '24px', height: '100%', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                {t('interpretation_label')}
                            </Typography>
                            {result?.risk && (
                                <Chip label={`${result.risk} Risk`} color={result.risk === 'High' ? 'error' : result.risk === 'Moderate' ? 'warning' : 'success'} sx={{ fontWeight: 'bold', borderRadius: 2 }} />
                            )}
                        </Box>
                        
                        <Typography variant="h6" sx={{ mb: 4, fontWeight: 'bold', color: 'text.primary', borderLeft: '4px solid', borderColor: 'primary.main', pl: 2 }}>
                            {result?.interpretation || scores.interpretation}
                        </Typography>

                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>{t('recommended_goals_title')}</Typography>
                        <List sx={{ mb: 2 }}>
                            {(result?.insights || []).map((insight, index) => (
                                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 32 }}><InfoIcon color="primary" fontSize="small" /></ListItemIcon>
                                    <ListItemText primary={insight} />
                                </ListItem>
                            ))}
                        </List>

                        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-around' }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>{scores.srs_score || 0}</Typography>
                                <Typography variant="caption" color="text.secondary">SRS SCORE</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>{scores.mchat_score || 0}</Typography>
                                <Typography variant="caption" color="text.secondary">M-CHAT SCORE</Typography>
                            </Box>
                        </Box>
                    </Card>
                </Grid>

                {/* Domain Breakdown */}
                <Grid item xs={12}>
                    <Card elevation={0} sx={{ p: 4, borderRadius: '24px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 4 }}>
                            {t('domain_specific_analysis', 'Domain Specific Analysis')}
                        </Typography>
                        <Grid container spacing={4}>
                            {[
                                { title: initialSections[0].title, score: scores.s1, max: initialSections[0].maxScore, colorKey: 'primary' },
                                { title: initialSections[1].title, score: scores.s2, max: initialSections[1].maxScore, colorKey: 'secondary' },
                                { title: initialSections[2].title, score: scores.s3, max: initialSections[2].maxScore, colorKey: 'warning' },
                                { title: initialSections[3].title, score: scores.s4, max: initialSections[3].maxScore, colorKey: 'error' },
                                { title: initialSections[4].title, score: scores.s5, max: initialSections[4].maxScore, colorKey: 'success' },
                            ].map((sec, i) => (
                                <Grid item xs={12} sm={6} md={3} key={i}>
                                    <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{sec.title}</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{sec.score || 0}/{sec.max}</Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={((sec.score || 0) / sec.max) * 100} 
                                        sx={{ 
                                            height: 6, 
                                            borderRadius: 3, 
                                            bgcolor: alpha(theme.palette[sec.colorKey].main, 0.1), 
                                            '& .MuiLinearProgress-bar': { bgcolor: `${sec.colorKey}.main` } 
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Card>
                </Grid>
            </Grid>

            <Box sx={{ mt: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                {/* Therapy Recommendations */}
                <Paper elevation={0} sx={{ p: 4, borderRadius: '16px', border: '1px solid #e0e0e0', width: '100%', maxWidth: '800px' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>{t('recommend_therapies') || 'Recommend Therapies'}</Typography>
                    <Grid container spacing={2}>
                        {['occupational', 'behavioral', 'speech'].map((therapy) => (
                            <Grid item xs={12} sm={4} key={therapy}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={recommendedTherapies.includes(therapy)}
                                            onChange={(e) => {
                                                if (e.target.checked) setRecommendedTherapies([...recommendedTherapies, therapy]);
                                                else setRecommendedTherapies(recommendedTherapies.filter(t => t !== therapy));
                                            }}
                                            color="primary"
                                        />
                                    }
                                    label={therapy.charAt(0).toUpperCase() + therapy.slice(1) + ' Therapy'}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                {/* Final Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button 
                        disabled={submitting}
                        variant="contained" 
                        size="medium"
                        sx={{ 
                            bgcolor: '#00bcd4', 
                            py: 1.2, 
                            px: 4, 
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            boxShadow: '0 4px 12px rgba(0, 188, 212, 0.2)',
                            '&:hover': { bgcolor: '#00acc1' }
                        }} 
                        onClick={handleSubmitInitial}
                    >
                        {submitting ? <CircularProgress size={24} color="inherit" /> : t('save_assessment_button')}
                    </Button>
                    <Button 
                        variant="outlined" 
                        size="medium" 
                        sx={{ py: 1.2, px: 4, borderRadius: '12px', fontWeight: 'bold' }} 
                        component={Link} 
                        to="/doctor-dashboard"
                    >
                        {t('back_to_dashboard_button')}
                    </Button>
                    <Button 
                        variant="text" 
                        onClick={() => navigate(`/child-profile/${childId}`)} 
                        size="medium"
                        sx={{ borderRadius: '12px', px: 2 }}
                    >
                        {t('view_profile_button')}
                    </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default AutismADHDAssessment;
